import { useCallback, useEffect, useRef, useState } from 'react';
import { noop } from 'lodash';
import { AdobeIMS } from '@identity/imslib';
import type { IEnvironment } from '@identity/imslib/adobe-id/IEnvironment';
import { CONFIG } from '@frameio/config';
import type { AdobeIMSConfig } from '@frameio/config/types';
import { useMeQuery } from '@frameio/graphql-client/generated';
import { ADOBE_IMS_V4_SCOPE } from './config';

export type ImsAdobeUser = {
  email: string;
  account_type: string;
  user_image_url: string;
  utcOffset: string;
  preferred_languages: string;
  displayName: string;
};

/**
 * Hook for Adobe IMS authentication.
 * Pass in a custom CONFIG to override the default Adobe IMS configuration.
 */
export const useAdobeIMS = ({
  config,
  debug = false,
  modalSignIn = false,
}: {
  config?: AdobeIMSConfig;
  debug?: boolean;
  modalSignIn?: boolean;
}) => {
  const IMS_CONFIG = config || CONFIG.adobeIMS.auth;

  // Initialize Adobe IMS on mount
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasCompletedModalSignIn, setHasCompletedModalSignIn] = useState(false);
  const adobeIMS = useRef<AdobeIMS>(null);
  const { data: meData } = useMeQuery({ returnPartialData: true });
  const me = meData?.me;
  const userEmail = me?.email || null;

  /**
   * Report console log error or warning messages.
   * Show warnings only in stg1 environments.
   */
  const reportLog = useCallback(
    ({
      msg,
      type,
      err = null,
    }: {
      msg: string;
      type: 'error' | 'warn';
      err?: unknown | null;
    }) => {
      // eslint-disable-next-line no-console
      if (type === 'error') console.error(msg, err);

      if (IMS_CONFIG.imsEnvironment === 'stg1' && type === 'warn')
        // eslint-disable-next-line no-console
        console.warn(msg);
    },
    [IMS_CONFIG.imsEnvironment]
  );

  /**
   * Gets the IMS access token from the current authenticated Adobe user
   * @param {AdobeIMS} AdobeIMS The AdobeIMS instance
   * @returns {Promise} A promise that resolves with the access token
   */
  const getImsAccessToken = useCallback(async () => {
    const token = await adobeIMS.current?.getAccessToken();
    return token;
  }, [adobeIMS]);

  const signOut = useCallback(async () => {
    const token = await adobeIMS.current?.getAccessToken();
    if (token) {
      await adobeIMS.current?.signOut();
      if (modalSignIn) {
        setHasCompletedModalSignIn(false);
      }
    }
  }, [adobeIMS, modalSignIn]);

  /**
   * Returns user Profile information of current user if logged in.
   */
  const getUserProfile = useCallback(async (): Promise<ImsAdobeUser> => {
    const profile = await adobeIMS.current?.getProfile();
    return profile;
  }, [adobeIMS]);

  /**
   * Redirects the user to Adobe IMS sign-in page.
   * ensure your redirect_uri is supported
   * with the Adobe IMS client id and matches existing redirect url patterns.
   *
   * @param {Object} optsOverrides Optional overrides for the sign-in options
   */
  const redirectToAdobeSignIn = useCallback(
    async (optsOverrides = {}) => {
      if (modalSignIn) {
        setHasCompletedModalSignIn(false);
      }
      if (!userEmail) {
        reportLog({
          msg: '`redirectToAdobeAndSignIn` expects an argument for `email`',
          type: 'warn',
        });
      }

      const defaultOpts = {
        // SUSI context id injection
        dctx_id: IMS_CONFIG.dynamicContextId,
        // Disable editing of the username field if `true`:
        eu: Boolean(userEmail),
        puser: userEmail,
        dc: false,
      };
      const opts = { ...defaultOpts, ...optsOverrides };

      /**
       * If intent is signup (not signin)
       * invalidate the user's current session first
       * to purge any stale session data.
       */
      if (opts.dc === false) await signOut();

      adobeIMS.current?.signIn(opts);
    },
    [
      IMS_CONFIG.dynamicContextId,
      adobeIMS,
      modalSignIn,
      reportLog,
      userEmail,
      signOut,
    ]
  );

  /**
   * Run on mount to create a Ref to the AdobeIMS instance.
   */
  useEffect(() => {
    if (adobeIMS.current) return;

    const ims = new AdobeIMS({
      client_id: IMS_CONFIG.clientId,
      environment: IMS_CONFIG.imsEnvironment as IEnvironment,
      // default redirect but can be overridden in `signIn` method
      redirect_uri: window.location.href,
      scope: [
        ...ADOBE_IMS_V4_SCOPE,
        ...(IMS_CONFIG.additionalScopes ?? []),
      ].join(','),
      ...(modalSignIn
        ? {
            modalMode: true,
            modalSettings: {
              width: 600,
              height: 700,
              top: 120,
              left: 120,
              allowedOrigin: window.location.origin,
            },
            onModalModeSignInComplete: () => {
              setHasCompletedModalSignIn(true);
              return true;
            },
          }
        : {}),
      onReady: () => {
        setIsInitialized(true);
      },
      onAccessToken: noop,
      onAccessTokenHasExpired: () => {
        reportLog({ msg: 'access token has expired', type: 'warn' });
      },
      onReauthAccessToken: () => {
        reportLog({ msg: 'reauth access token', type: 'warn' });
      },
      onError: (error) => {
        /**
         * IMS error message table:
         * https://wiki.corp.adobe.com/display/ABPSup/IMS+Error+Extensions
         */
        reportLog({ msg: 'error', err: error, type: 'error' });
      },
      locale: navigator.language || 'en-US',
    });

    if (debug) ims.enableLogging();

    ims.initialize();

    adobeIMS.current = ims;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    adobeIMS: adobeIMS.current,
    getAccessToken: getImsAccessToken,
    getUser: getUserProfile,
    initialized: isInitialized,
    signIn: redirectToAdobeSignIn,
    signOut,
    ...(modalSignIn ? { hasCompletedModalSignIn } : {}),
  };
};
